import { DataTable, DataTableColumnDef } from "@shared/EDS/components/DataTable/DataTable";
import { legacyCreateColumnHelper } from "@tanstack/react-table/legacy";
import {
  PlanTrainUnitDetails,
  TransitionType,
  MatchingChangeability,
  sortMatchingChangeability,
} from "@opstel/query/models/planTrainUnitDetails/planTrainUnitDetails";
import { DATETIME_FORMAT_NO_DATE } from "@shared/common/utils/luxonHelpers";
import { RadioPanel, RadioPanelItem } from "@shared/EDS/components/RadioPanel/RadioPanel";
import { Toolbar } from "@shared/EDS/components/Toolbar";
import { useCallback, useMemo, useState } from "react";
import { TransitionIcon } from "../ServiceTasksDialog/TransitionIcon";
import { ClaimsCell } from "../ServiceTasksDialog/ClaimsCell";

export interface TrainUnitTransitionsProps {
  readonly planTrainUnitDetails: PlanTrainUnitDetails[];
  readonly isLoading?: boolean;
  readonly toggleTransition: () => TogglePinneddStateFn;
}
export type TogglePinneddStateFn = (params: TogglePinnedStateParameters) => void;
export interface TogglePinnedStateParameters {
  planTrainUnitGuid: string;
  newPinnedState: MatchingChangeability;
}

export function TrainUnitTransitions({
  planTrainUnitDetails,
  isLoading = false,
  toggleTransition,
}: TrainUnitTransitionsProps) {
  const [selectedFilter, setSelectedFilter] = useState<TransitionFilterType>("ALL");

  const planTrainUnitDetailsFiltered = useMemo(() => {
    if (selectedFilter === "ALL") {
      return planTrainUnitDetails;
    }
    return planTrainUnitDetails.filter((task) => task.transition === selectedFilter);
  }, [planTrainUnitDetails, selectedFilter]);

  const handleCheckedChange = useCallback(
    (row: PlanTrainUnitDetails) => {
      toggleTransition()({
        planTrainUnitGuid: row.ptuGuid,
        newPinnedState: row.matchingChangeability === "PINNED" ? "OPEN" : "PINNED",
      });
    },
    [toggleTransition],
  );

  const filterOptions: RadioPanelItem<TransitionFilterType>[] = [
    { label: "Alles", value: "ALL" },
    { label: "Nacht", value: "NIGHT" },
    { label: "Dag", value: "DAY" },
    { label: "Overig", value: "NIGHT_OUTSIDE_SCENARIO" },
  ];

  const columnHelper = legacyCreateColumnHelper<PlanTrainUnitDetails>();
  const columns: DataTableColumnDef<PlanTrainUnitDetails>[] = [
    columnHelper.group({
      id: "appearing",
      header: "Aankomst",
      columns: [
        {
          id: "appearingTrainUnitName",
          header: "Treinnummer",
          accessorKey: "appearingTrainUnitName",
        },
        {
          id: "appearingTime",
          header: "Aankomsttijd",
          accessorKey: "appearingTime",
          cell: ({ row }) => row.original.appearingTime?.toFormat(DATETIME_FORMAT_NO_DATE),
        },
        {
          id: "appearingTrainTrackName",
          header: "Spoor",
          accessorKey: "appearingTrackName",
        },
      ],
      meta: { className: "table-header-emphasized" },
    }),
    columnHelper.group({
      id: "disappearing",
      header: "Vertrek",
      columns: [
        {
          id: "disappearingTrainUnitName",
          header: "Treinnummer",
          accessorKey: "disappearingTrainUnitName",
          meta: { className: "with-border" },
        },
        {
          id: "disappearingTime",
          header: "Vertrektijd",
          accessorKey: "disappearingTime",
          cell: ({ row }) => row.original.disappearingTime?.toFormat(DATETIME_FORMAT_NO_DATE),
        },
        {
          id: "disappearingTrainTrackName",
          header: "Spoor",
          accessorKey: "disappearingTrackName",
        },
      ],
      meta: { className: "table-header-emphasized with-border" },
    }),
    columnHelper.group({
      id: "transition",
      header: "Overgang",
      columns: [
        {
          id: "trainUnitKind",
          header: "Materieel",
          accessorKey: "trainUnitKind",
          meta: { className: "with-border" },
        },
        {
          id: "transition",
          header: "Overgang",
          accessorKey: "transition",
          cell: ({ row }) => <TransitionIcon transition={row.original.transition} />,
        },
        {
          id: "transitionDuration",
          header: "Overstand (u)",
          accessorKey: "transitionDuration",
          cell: ({ row }) => row.original.transitionDuration.toFormat("hh:mm"),
        },
        {
          id: "claims",
          header: "Claims",
          accessorKey: "claims",
          cell: ({ row }) => <ClaimsCell claims={row.original.claims} />,
          sortFn: (a, b) => {
            const aClaims = a.original.claims.length;
            const bClaims = b.original.claims.length;
            return aClaims - bClaims;
          },
        },
        {
          id: "matchingChangeAbility",
          header: "Vastzetten",
          accessorKey: "matchingChangeAbility",
          cell: ({ row }) => {
            return row.original.matchingChangeability === "FIXED" ? (
              <tractie-icon
                name="lock"
                color="--tractie-color-content-emphasis"
                title="Vaste overgang"
              />
            ) : (
              <tractie-toggle
                data-testid={`train-unit-transition-toggle-${row.original.ptuGuid}`}
                className="relative top-[5px]"
                onClick={() => handleCheckedChange(row.original)}
                checked={row.original.matchingChangeability === "PINNED"}
              />
            );
          },
          sortFn: (a, b) => {
            return sortMatchingChangeability(
              a.original.matchingChangeability,
              b.original.matchingChangeability,
            );
          },
        },
      ],
      meta: { className: "table-header-emphasized with-border" },
    }),
  ];

  return (
    <>
      <Toolbar>
        <div className="mb-m flex w-full items-center justify-between">
          <RadioPanel items={filterOptions} onChange={setSelectedFilter} defaultValue="ALL" />
        </div>
      </Toolbar>
      <DataTable
        data-testid="train-unit-transitions-table"
        className="train-unit-transitions-table"
        columns={columns}
        data={planTrainUnitDetailsFiltered}
        defaultSort={[{ id: "appearingTime", desc: false }]}
        options={{
          isRowInteractive: () => false,
          emptyMessage: "Geen materieelovergangen beschikbaar.",
          loadingMessage: "Materieelovergangen worden geladen...",
        }}
        isLoading={isLoading}
      />
    </>
  );
}

type TransitionFilterType = TransitionType | "ALL";
